import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckSquare, Copy, History, Loader2, RotateCcw, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import {
  cloneDocIntelligenceTemplate,
  confirmDocIntelligenceProjectStatic,
  getDocIntelligenceTemplateVersions,
  restoreDocIntelligenceTemplateVersion
} from '../../services/api/implementation/impl';
import { btnPrimary, btnSecondary } from './DocIntelUi';
import { formatAbsoluteTime, formatRelativeTime } from './docIntelUtils';

const PROJECT_STATIC_ITEMS = [
  { id: 'land_recitals', label: 'Land / WHEREAS recitals match this project' },
  { id: 'rera_registration', label: 'RERA registration number confirmed' },
  { id: 'commencement_jda', label: 'Commencement / JDA / layout approvals updated' },
  { id: 'city_of_execution', label: 'City of execution / jurisdiction matches' }
];

const emptyChecklist = () =>
  PROJECT_STATIC_ITEMS.reduce((acc, item) => {
    acc[item.id] = false;
    return acc;
  }, {});

const DocIntelTemplateLifecycle = ({
  templateId,
  currentProjectId,
  isPromoted,
  projectStaticChecklist,
  projectStaticConfirmedAt,
  onRestored,
  onCloned,
  onProjectStaticConfirmed
}) => {
  const queryClient = useQueryClient();
  const { projects = [] } = useProjectsList();
  const [cloneProjectId, setCloneProjectId] = useState('');
  const [checklist, setChecklist] = useState(() => ({
    ...emptyChecklist(),
    ...(projectStaticChecklist || {})
  }));

  useEffect(() => {
    setChecklist({
      ...emptyChecklist(),
      ...(projectStaticChecklist || {})
    });
  }, [projectStaticChecklist, templateId]);

  const versionsQuery = useQuery({
    queryKey: ['docIntelTemplateVersions', templateId],
    queryFn: () => getDocIntelligenceTemplateVersions(templateId),
    enabled: Boolean(templateId)
  });

  const restoreMutation = useMutation({
    mutationFn: (revisionId) =>
      restoreDocIntelligenceTemplateVersion(templateId, revisionId),
    onSuccess: (template) => {
      toast.success('Version restored as a new revision');
      queryClient.invalidateQueries({ queryKey: ['docIntelTemplateVersions', templateId] });
      onRestored?.(template);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Restore failed');
    }
  });

  const cloneMutation = useMutation({
    mutationFn: () =>
      cloneDocIntelligenceTemplate(templateId, Number(cloneProjectId)),
    onSuccess: (template) => {
      toast.success('Cloned — confirm project-specific clauses on the new draft before promote');
      setCloneProjectId('');
      queryClient.invalidateQueries({
        queryKey: ['docIntelTemplates', Number(template.project_id)]
      });
      onCloned?.(template);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Clone failed');
    }
  });

  const confirmMutation = useMutation({
    mutationFn: () => confirmDocIntelligenceProjectStatic(templateId, checklist),
    onSuccess: (template) => {
      toast.success('Project-specific clauses confirmed');
      onProjectStaticConfirmed?.(template);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Confirm failed');
    }
  });

  const versions = versionsQuery.data ?? [];
  const cloneTargets = projects.filter((project) => Number(project.id) !== Number(currentProjectId));
  const allChecked = useMemo(
    () => PROJECT_STATIC_ITEMS.every((item) => checklist[item.id] === true),
    [checklist]
  );
  const isConfirmed = Boolean(projectStaticConfirmedAt) && allChecked;

  return (
    <div className="space-y-3">
      {!isPromoted && (
        <section className="rounded-xl border border-slate-200 bg-white px-3.5 py-3">
          <div className="mb-3 flex items-start gap-2.5">
            <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-800">
              <ShieldCheck className="h-3.5 w-3.5" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900">Project-specific clauses</p>
              <p className="mt-0.5 text-xs font-medium leading-snug text-slate-600">
                Required before Promote — land, RERA, approvals, and city must match{' '}
                <strong>this</strong> project (especially after clone).
              </p>
            </div>
          </div>
          <ul className="space-y-2">
            {PROJECT_STATIC_ITEMS.map((item) => (
              <li key={item.id}>
                <label className="flex cursor-pointer items-start gap-2 text-[12px] text-slate-700">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={checklist[item.id] === true}
                    disabled={isConfirmed || confirmMutation.isPending}
                    onChange={(e) =>
                      setChecklist((prev) => ({ ...prev, [item.id]: e.target.checked }))
                    }
                  />
                  <span>{item.label}</span>
                </label>
              </li>
            ))}
          </ul>
          {isConfirmed ? (
            <p className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-800">
              <CheckSquare className="h-3.5 w-3.5" />
              Confirmed {formatAbsoluteTime(projectStaticConfirmedAt)}
            </p>
          ) : (
            <button
              type="button"
              disabled={!allChecked || confirmMutation.isPending}
              onClick={() => confirmMutation.mutate()}
              className={`${btnPrimary} mt-3 w-full justify-center`}>
              {confirmMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ShieldCheck className="h-4 w-4" />
              )}
              Confirm for this project
            </button>
          )}
        </section>
      )}

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex items-start gap-2.5 border-b border-slate-100 px-3.5 py-3">
          <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-teal-50 text-teal-800">
            <History className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">Version history</p>
            <p className="mt-0.5 text-xs font-medium leading-snug text-slate-600">
              Every save creates a recoverable revision.
            </p>
          </div>
        </div>
        <div className="max-h-[min(420px,50vh)] overflow-y-auto">
          {versionsQuery.isLoading ? (
            <p className="flex items-center gap-2 px-3.5 py-5 text-xs text-slate-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading versions…
            </p>
          ) : versions.length ? (
            <ul className="divide-y divide-slate-100">
              {versions.map((revision, index) => (
                <li
                  key={revision.id}
                  className="flex items-start justify-between gap-2 px-3.5 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-1.5 text-xs font-medium text-slate-800">
                      <span>Version {revision.version}</span>
                      {index === 0 && (
                        <span className="rounded-full bg-teal-50 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-teal-700">
                          Current
                        </span>
                      )}
                    </p>
                    <p className="mt-1 text-xs font-medium capitalize text-slate-700">
                      {String(revision.change_type || 'save').replace(/_/g, ' ')}
                    </p>
                    <p className="mt-0.5 text-xs font-medium text-slate-600">
                      {formatAbsoluteTime(revision.created_at)}
                      <span className="text-slate-400">
                        {' '}
                        · {formatRelativeTime(revision.created_at)}
                      </span>
                    </p>
                  </div>
                  {index > 0 && !isPromoted && (
                    <button
                      type="button"
                      disabled={restoreMutation.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Restore version ${revision.version}? Your current content remains in history.`
                          )
                        ) {
                          restoreMutation.mutate(revision.id);
                        }
                      }}
                      className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-bold text-teal-800 hover:bg-teal-50 disabled:opacity-50">
                      <RotateCcw className="h-3 w-3" /> Restore
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-3.5 py-5 text-xs text-slate-500">No saved versions yet.</p>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white px-3.5 py-3">
        <div className="mb-3 flex items-start gap-2.5">
          <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-teal-50 text-teal-800">
            <Copy className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">Clone to another project</p>
            <p className="mt-0.5 text-xs font-medium leading-snug text-slate-600">
              Creates an independent draft. Project-static confirmation resets — you must re-confirm
              before promote.
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <select
            value={cloneProjectId}
            onChange={(event) => setCloneProjectId(event.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800">
            <option value="">Select target project</option>
            {cloneTargets.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!cloneProjectId || cloneMutation.isPending}
            onClick={() => cloneMutation.mutate()}
            className={`${btnSecondary} w-full justify-center`}>
            {cloneMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            Clone
          </button>
        </div>
        {!cloneTargets.length && (
          <p className="mt-2 text-xs font-medium text-slate-600">
            No other accessible projects are available.
          </p>
        )}
      </section>
    </div>
  );
};

export default DocIntelTemplateLifecycle;
